import { supabase } from '../lib/supabase';

function mapProject(row) {
  const sorted = [...(row.project_sections || [])].sort((a, b) => a.order_index - b.order_index);
  const get = (type) => sorted.find((s) => s.section_type === type)?.content || '';
  const coverColor = sorted.find((s) => s.section_type === 'cover')?.background_color || '#EBE8E3';

  const keyScreens = sorted
    .filter((s) => s.section_type === 'screen' && s.image_url && s.is_visible !== false)
    .map((s) => ({ src: s.image_url, caption: s.title || '' }));

  // 콘텐츠 섹션(텍스트) — cover/short_description/image/screen 제외.
  // 'image' 타입은 순수 이미지 전용 섹션이라 title(=alt 텍스트)만 있고 본문이 없는 경우가 많아,
  // 여기 포함되면 상세 페이지·팝업에 빈 제목만 있는 엉뚱한 섹션으로 보이므로 제외한다.
  const SYSTEM_TYPES = ['cover', 'short_description', 'image', 'screen'];
  const textSections = sorted
    .filter((s) => !SYSTEM_TYPES.includes(s.section_type) && s.is_visible !== false)
    .map((s) => ({
      type:       s.section_type,
      title:      s.title || '',
      content:    s.content || '',
      videoUrl:   s.video_url || null,
      textSize:   s.text_size || 'md',
      textWeight: s.text_weight || 'regular',
      images: [
        ...(s.image_url ? [{ src: s.image_url, alt: s.title || '' }] : []),
        ...((s.extra_images || []).map((img) =>
          typeof img === 'string'
            ? { src: img, alt: '' }
            : { src: img.url || '', alt: img.alt || '' }
        )),
      ].filter((img) => img.src),
    }));

  // 카드 썸네일 / 상세 히어로용 대표 이미지 — thumbnail_url 우선, 없으면 legacy 'image' 섹션
  const legacyImages = sorted
    .filter((s) => s.section_type === 'image' && s.image_url && s.is_visible !== false)
    .map((s) => ({ src: s.image_url, alt: s.title || '' }));
  const images = row.thumbnail_url
    ? [{ src: row.thumbnail_url, alt: row.title }]
    : legacyImages;

  // 갤러리 팝업(IllustrationModal)용 이미지 — 이름을 자유롭게 지은 일반 섹션에 첨부된 이미지까지
  // 전부 순서대로 모음 (cover/short_description/screen 제외). 카드 썸네일 등 다른 곳에 쓰이는
  // images와는 분리해서, 콘텐츠 섹션 이미지가 늘어나도 대표 이미지 선택 로직에 영향 없게 함
  const galleryImages = sorted
    .filter((s) => !['cover', 'short_description', 'screen'].includes(s.section_type) && s.is_visible !== false)
    .flatMap((s) => [
      ...(s.image_url ? [{ src: s.image_url, alt: s.title || '' }] : []),
      ...((s.extra_images || []).map((img) =>
        typeof img === 'string'
          ? { src: img, alt: '' }
          : { src: img.url || '', alt: img.alt || '' }
      )),
    ])
    .filter((img) => img.src);

  return {
    id:               row.id,
    type:             row.project_type || 'uiux',
    title:            row.title,
    role:             row.role || '',
    year:             row.project_date ? String(new Date(row.project_date).getFullYear()) : '',
    tags:             row.tags || [],
    coverColor,
    // 카드/캐러셀 대표 이미지: thumbnail_url 우선 → 없으면 legacy 'image' 섹션
    images,
    // 갤러리 팝업 전용: 콘텐츠 섹션 이미지 → 없으면 위 images로 폴백
    galleryImages:    galleryImages.length ? galleryImages : images,
    mobileHeroImage:  row.mobile_thumbnail_url || null,
    isMainOnly:       row.is_main_only || false,
    isVisible:        row.is_visible !== false,
    externalUrl:      row.external_url || null,
    linkType:         row.link_type || 'download',
    shortDescription: get('short_description'),
    sections:         textSections,
    overview:         get('overview'),
    problem:          get('problem'),
    solution:         get('solution'),
    iaFlow:           get('ia_flow'),
    outcome:          get('outcome'),
    summary:          get('summary'),
    description:      get('description'),
    keyScreens,
    workPeriod:       row.work_period || '',
    workType:         row.work_type || '',
    contribution:     row.contribution || '',
    tools:            row.tools || '',
    linkUrl:          row.link_url || '',
  };
}

const SELECT = `
  id, title, project_type, tags, role, project_date, thumbnail_url, mobile_thumbnail_url,
  is_visible, is_main_only, external_url, link_type,
  work_period, work_type, contribution, tools, link_url,
  project_sections(section_type, content, image_url, extra_images, title, order_index, background_color, is_visible, video_url, text_size, text_weight)
`;

// Work 페이지에서 제외할 타입
const WORK_EXCLUDE_TYPES = ['log', 'download'];

export async function fetchProjects(type = 'all') {
  let query = supabase.from('projects').select(SELECT)
    .eq('is_visible', true)
    .order('project_date', { ascending: false });

  if (type && type !== 'all') {
    query = query.eq('project_type', type);
  } else {
    // 'all' 일 때 log·download는 Work 페이지에서 제외
    query = query.not('project_type', 'in', `(${WORK_EXCLUDE_TYPES.join(',')})`);
  }

  const { data, error } = await query;
  if (error) { console.error('[projectService] fetchProjects:', error); return []; }
  return (data || []).map(mapProject);
}

export async function fetchProjectById(id) {
  if (!id || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return null;
  const { data, error } = await supabase.from('projects').select(SELECT).eq('id', id).single();
  if (error) { console.error('[projectService] fetchProjectById:', error); return null; }
  return data ? mapProject(data) : null;
}

export async function fetchFeaturedProjects() {
  const { data, error } = await supabase
    .from('home_featured_projects')
    .select(`
      is_visible, order_index, button_label, button_url, button_target,
      projects(
        id, title, project_type, tags, role, project_date, thumbnail_url, mobile_thumbnail_url,
        is_visible, is_main_only,
        project_sections(section_type, content, image_url, extra_images, title, order_index, background_color)
      )
    `)
    .eq('is_visible', true)
    .order('order_index', { ascending: true });

  if (!error && data && data.length > 0) {
    return data
      .filter((item) => item.projects?.id && item.projects?.is_visible !== false)
      .map((item) => {
        const proj = Array.isArray(item.projects) ? item.projects[0] : item.projects;
        return {
          ...mapProject(proj),
          buttonLabel:  item.button_label,
          buttonUrl:    item.button_url,
          buttonTarget: item.button_target,
        };
      });
  }

  // 피처드 없으면 최신 노출 프로젝트 4개 폴백
  const { data: fallback, error: fbErr } = await supabase
    .from('projects').select(SELECT)
    .eq('is_visible', true)
    .order('project_date', { ascending: false }).limit(4);
  if (fbErr) { console.error('[projectService] fetchFeaturedProjects fallback:', fbErr); return []; }
  return (fallback || []).map(mapProject);
}
